import { EventEmitter, Injectable } from '@angular/core';
import { ApiClientService } from './api-client.service';
import { UrlService } from './url.service';
import { EmptyRowService } from './empty-row.service';
import { AccountPermissionsService } from './account-permissions.service';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { WebsiteBuilder } from '../types/website-builder/website-builder';
import { WebsiteBuilderDocuments } from '../types/website-builder/website-builder-documents';
import { AnnouncementApiService } from './api/announcement-api.service';
import { AuthApiService } from './api/auth-api.service';
import { AlertService } from './alert.service';
import { IAnnouncementCnSettings } from '../types/api-response/announcements/announcementCnSettings';
import { switchMap } from 'rxjs/operators';
import { IWebsiteBuilder } from '../types/api-response/auth/website-builder';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { WebsitePage } from '../types/website-builder/website-page';
import { AmenityApiService } from './api/amenity-api.service';
import { WebsiteBuilderItemFeatures } from '../types/website-builder/items';

declare const $: any;

@Injectable()
export class WebsiteBuilderService {
    frameWindow: Window;

    private websiteBuilderExpandParams = [
        'websiteAmenities',
        'websiteWelcome',
        'websiteWelcomeMessage',
        'websiteAboutUs',
        'websiteGallery',
        'websiteBuildingFeatures',
        'websiteNeighbourhood',
        'websiteContact',
        'websiteDocument',
        'websiteAnnouncements',
        'websiteGallery.galleryImages',
        'websiteBuildingFeatures.buildingFeaturesImages',
        'websiteContact.phones',
        'websiteContact.socialNetworks',
        'updatedBy'
    ].join(',');

    data: {
        data: WebsiteBuilder;
        total: number;
        corporations: {
            get: string;
            data: IWebsiteBuilder[]
            total: number;
        }
    } = {
        data: null,
        total: null,
        corporations: {
            get: `/api/v1/website_builder.json?expand=${this.websiteBuilderExpandParams}`,
            data: [],
            total: null
        }
    };
    tabId = 'websiteBuilder';
    spinnerShow = true;
    showServiceUnavailable = false;
    serviceName: string = 'website_builder';
    onAddressChange: EventEmitter<any> = new EventEmitter();
    toggleStreetView: EventEmitter<any> = new EventEmitter();
    fullScreenPreview = false;
    showCorporationsList = true;
    showWebsiteBuilderView = false;
    selectedItemData: any;
    chosenCorporation: any;

    static getTree(data, parent) {
        const result = [];
        data.forEach(function (el) {
            const item = el;
            if (parseInt(item.parent) === parseInt(parent)) {
                item.children = WebsiteBuilderService.getTree(data, item.id);
                result.push(item);
            }
        });
        return result;
    }

    constructor(
        public api: ApiClientService,
        private authApi: AuthApiService,
        private announcementApi: AnnouncementApiService,
        private amenityApi: AmenityApiService,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService,
        private alert: AlertService,
    ) {
    }

    getDocuments(corporationId: number) {
        this.api.get('domainDocuments', `/api/v1/documents.json?per-page=1000&is_public=1&corporation_number_id=${corporationId}`)
            .map(response => response.json())
            .map((res) => {
                try {
                    return <WebsiteBuilderDocuments[]>res.entities.map((data) => {
                        const list = res.entities.filter(function (t, el) {
                            return parseInt(t.id) === parseInt(data.parent);
                        });
                        if (list.length === 0) {
                            data.parent = 0;
                        }
                        return new WebsiteBuilderDocuments(data);
                    });
                } catch (e) {
                    console.log('Bad Response', e);
                    return of([]);
                }
            })
            .map(res => WebsiteBuilderService.getTree(res, 0))
            .subscribe((res: WebsiteBuilderDocuments[]) => {
                res.sort(function (a, b) {
                    return a.name > b.name ? 1 : -1;
                });
                res.sort(function (a, b) {
                    return a.type === b.type ? 0 : (a.type > b.type ? 1 : -1);
                });
                this.data.data.publicDocuments = res;
            });
    }

    getData(item: IWebsiteBuilder) {
        this.data.data = new WebsiteBuilder(item);

        // Receive notices list
        this.announcementApi.getNoticesByCorporationNumberId(item.corporationNumberId).subscribe(result => {
            this.data.data.announcements = result.entities;
            this.data.data.selectedNoticeId = result.entities.length ? result.entities[0].id : null;
        });

        // Receive public amenities list
        this.amenityApi.getPublicCorporationAmenities(item.corporationNumberId).subscribe(result => {
            const amenityIds = (item.websiteAmenities && item.websiteAmenities.amenitiesIds) || [];
            this.data.data.publicAmenities = result.entities.map(amenity => ({...amenity, checked: amenityIds.includes(amenity.id)}));
        });

        // Receive documents list
        this.getDocuments(item.corporationNumberId);

        this.showCorporationsList = false;
        this.showWebsiteBuilderView = true;
    }

    deactivateSiteBuilderPage(): Observable<boolean> {
        return of(true);
    }

    sendData(): void {
        const data: any = {
            id: this.data.data.id,
            corporationNumberId: this.chosenCorporation,
            ...this.data.data.value
        };

        forkJoin([
            this.authApi.setWebsiteBuilder(data),
            this.announcementApi.getCorporationNumberSettings((data as IWebsiteBuilder).corporationNumberId)
        ]).pipe(
            switchMap(([website, cnSettings]) => {
                if(!website.websiteUrl) {
                    this.alert.toast.warning('Website URL is not defined');

                    return of(true); // cant't set settings with empty website url
                }

                // add URL schema if not exist
                const websiteUrl = website.websiteUrl.substr(0, 4) !== 'http' ? `https://${website.websiteUrl}` : website.websiteUrl;

                if(!cnSettings) { // create default CN settings if not defined
                    cnSettings = {
                        corporationNumberId: website.corporationNumberId,
                        corporationNumberName: website.corporationNumber,
                        corporationNumberWebsite: websiteUrl,
                        senderEmail: '',
                        confirmationSubject: '',
                    };
                } else { // or update CN name and website URL for existing settings
                    cnSettings.corporationNumberName = website.corporationNumber;
                    cnSettings.corporationNumberWebsite = websiteUrl;
                }

                return this.announcementApi.setCorporationNumberSettings(cnSettings);
            })
        ).subscribe(
            () => this.alert.toast.success('Website was successfully updated!'),
            () => this.alert.toast.error('Failed to save website settings')
        );
    }

    resetData() {
        this.data.corporations.data = [];
        this.data.corporations.total = null;
        this.showCorporationsList = true;
        this.showWebsiteBuilderView = false;
        this.selectedItemData = null;
        this.chosenCorporation = null;
    }

    canEdit(buildingId?: number) {
        return this.account.canUpdate(this.serviceName, buildingId);
    }

    canDelete(buildingId?: number) {
        return this.account.canDelete(this.serviceName, buildingId);
    }

    getSplittedArraysOfImages(images): Array<any> {
        const imagesCopy = JSON.parse(JSON.stringify(images));
        const returnArray = [];
        while (imagesCopy.length) {
            returnArray.push(imagesCopy.splice(0, 3));
        }
        return returnArray;
    }

    refreshSplittedArray(): void {
        const buildingFeaturesImages = (this.data.data.websitePages[WebsitePage.buildingFeatures] as WebsiteBuilderItemFeatures).buildingFeaturesImages;
        (this.data.data.websitePages[WebsitePage.buildingFeatures] as WebsiteBuilderItemFeatures).buildingFeaturesImagesSplitted = this.getSplittedArraysOfImages(buildingFeaturesImages);
    }

    getCorporations(scrollToTop?): void {
        this.showServiceUnavailable = false;
        this.spinnerShow = true;

        this.authApi.getWebsiteBuilderList({...this.urlService.queryParams, expand: this.websiteBuilderExpandParams}).subscribe(
            (result) => {
                this.data.corporations.data = result.entities;
                this.data.corporations.total = result.total;
                this.emptyRowService.fillEmptyRow();
                if ($('.table')[0] && scrollToTop)
                    $('.table')[0].scrollIntoView();
            },
            (error) => {
                this.showServiceUnavailable = true;
                this.spinnerShow = false;
                this.api.errorHandler(error, 'Failed get corporations');
            },
            () => {
                this.spinnerShow = false;
                this.showCorporationsList = true;
            }
        );
    }

    toggleDetailedView(data: IWebsiteBuilder, state: boolean) {
        if (state) {
            this.getData(data);
        } else {
            this.getCorporations();
        }

        this.showWebsiteBuilderView = state;
        this.showCorporationsList = !state;
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/website-builder.service.ts